import { Action } from 'redux-actions';
import {
	ADD_SERVICE,
	SET_SELECTED_SERVICES,
	REMOVE_SERVICE,
	CLEAR_SERVICE,
	EQUALIZE_PASSENGER_BAGGAGE,
	EqualizePassengerBaggagePayload,
	ReplaceServicePayload,
	REPLACE_SERVICE,
	SET_SERVICE
} from './actions';
import { ModifiedService } from '../../types';
import { getRelatedSegments } from '../../utils';
import { ServicePayload } from '../../../../Checkout/store/selectedServices/actions';
import { CheckinPassengerFragment } from '@websky/graphql';

export interface ServicesBySegments {
	[segmentId: string]: number;
}

export interface PassengerServices {
	[serviceId: string]: ServicesBySegments;
}

export interface SelectedServicesState {
	[passengerId: string]: PassengerServices;
}

export const setServicesReducer = (state: SelectedServicesState, services: ServicePayload[]) => {
	let newState = {
		...state
	};

	services.forEach(service => {
		newState = {
			...newState,
			[service.passengerId]: {
				...(newState[service.passengerId] ?? {}),
				[service.serviceId]: {
					...(newState[service.passengerId]?.[service.serviceId] ?? {}),
					[service.segmentId]: service.count
				}
			}
		};
	});

	return newState;
};

export const addServiceReducer = (
	state: SelectedServicesState,
	{ passengerId, service, segmentId, count = 1 }: ModifiedService
): SelectedServicesState => {
	// List of passengers for which given service is ALLOWED to be added.
	const allowedPassengers = new Set(service.allowedPassengers.map(group => group[0]));
	// List of segments for which given service MUST be added.
	// Sometimes service should be added to a multiple segments at the same time.
	// This happens when service is tightly bound to a whole trip itself (to a "leg" in terms of Nemo),
	// rather than to a particular segment.
	const segmentsToAdd = getRelatedSegments(service.allowedSegments, segmentId);

	if (allowedPassengers.has(passengerId) && segmentsToAdd.size > 0) {
		// Deep copy of the state object.
		const result: SelectedServicesState = {
			...state,
			[passengerId]: {
				...state[passengerId],
				[service.id]: {
					...state[passengerId]?.[service.id]
				}
			}
		};

		segmentsToAdd.forEach(segmentId => {
			const serviceCount = result[passengerId][service.id][segmentId] ?? 0;
			result[passengerId][service.id][segmentId] = serviceCount + count;
		});

		return result;
	}

	return state;
};

export const removeServiceReducer = (
	state: SelectedServicesState,
	{ passengerId, service, segmentId }: ModifiedService
): SelectedServicesState => {
	// Check if we have any selected services.
	const couldBeDecreased = state[passengerId]?.[service.id]?.[segmentId] > 0;

	if (couldBeDecreased) {
		// List of segments for which given service MUST be decreased.
		// Sometimes service should be decreased for multiple segments at the same time.
		// This happens when service is tightly bound to a whole trip itself (to a "leg" in terms of Nemo),
		// rather than to a particular segment.
		const segmentsToDecrease = getRelatedSegments(service.allowedSegments, segmentId);

		if (segmentsToDecrease.size > 0) {
			// Deep copy of the state object.
			const result: SelectedServicesState = {
				...state,
				[passengerId]: {
					...state[passengerId],
					[service.id]: {
						...state[passengerId]?.[service.id]
					}
				}
			};

			segmentsToDecrease.forEach(segmentId => {
				const serviceCount = result[passengerId]?.[service.id]?.[segmentId];

				if (serviceCount > 0) {
					result[passengerId][service.id][segmentId] = serviceCount - 1;
				}
			});

			return result;
		}
	}

	return state;
};

export const setSelectedServicesReducer = (passengers: CheckinPassengerFragment[]): SelectedServicesState => {
	const result: SelectedServicesState = {};

	passengers.forEach(passenger => {
		const services = passenger?.services?.gdsServices?.services;
		const passengerId = passenger.id;

		if (services) {
			services.forEach(({ segmentIds, serviceId, count }) => {
				if (!result.hasOwnProperty(passengerId)) {
					result[passengerId] = {};
				}

				if (!result[passengerId].hasOwnProperty(serviceId)) {
					result[passengerId][serviceId] = {};
				}

				segmentIds.forEach(segmentId => {
					if (!result[passengerId][serviceId].hasOwnProperty(segmentId)) {
						result[passengerId][serviceId][segmentId] = 0;
					}

					result[passengerId][serviceId][segmentId] += count;
				});
			});
		}
	});

	return result;
};

export const clearServiceReducer = (
	state: SelectedServicesState,
	{ passengerId, service, segmentId }: ModifiedService
): SelectedServicesState => {
	// Check if we have any selected services.
	const couldBecleared = state[passengerId]?.[service.id]?.[segmentId] > 0;

	if (couldBecleared) {
		// List of segments for which given service MUST be decreased.
		// Sometimes service should be decreased for multiple segments at the same time.
		// This happens when service is tightly bound to a whole trip itself (to a "leg" in terms of Nemo),
		// rather than to a particular segment.
		const segmentsToClear = getRelatedSegments(service.allowedSegments, segmentId);

		if (segmentsToClear.size > 0) {
			// Deep copy of the state object.
			const result: SelectedServicesState = {
				...state,
				[passengerId]: {
					...state[passengerId],
					[service.id]: {
						...state[passengerId]?.[service.id]
					}
				}
			};

			segmentsToClear.forEach(segmentId => {
				result[passengerId][service.id][segmentId] = 0;
			});

			return result;
		}
	}

	return state;
};

export const equalizePassengerBaggageReducer = (
	state: SelectedServicesState,
	{ passengerId, baggageList, segmentsList }: EqualizePassengerBaggagePayload
): SelectedServicesState => {
	if (state.hasOwnProperty(passengerId)) {
		const result: SelectedServicesState = {
			...state,
			[passengerId]: {
				...state[passengerId]
			}
		};

		const passengerServices = result[passengerId];

		for (const serviceId in passengerServices) {
			if (passengerServices.hasOwnProperty(serviceId) && baggageList.has(serviceId)) {
				const servicesBySegments = passengerServices[serviceId];
				let maxServiceCount = 0;

				// Find the max count of selected services.
				for (const segmentId in servicesBySegments) {
					if (
						servicesBySegments.hasOwnProperty(segmentId) &&
						maxServiceCount < servicesBySegments[segmentId]
					) {
						maxServiceCount = servicesBySegments[segmentId];
					}
				}

				// Set found max service count for all segments.
				segmentsList.forEach(segmentId => (result[passengerId][serviceId][segmentId] = maxServiceCount));
			}
		}

		return result;
	}

	return state;
};

export const replaceServiceReducer = (
	state: SelectedServicesState,
	{ newService, oldService, passengerId, segmentsList }: ReplaceServicePayload
): SelectedServicesState => {
	if (state.hasOwnProperty(passengerId)) {
		let hasOldServices = false;

		const result: SelectedServicesState = {
			...state,
			[passengerId]: {
				...state[passengerId]
			}
		};

		if (result[passengerId].hasOwnProperty(oldService.id)) {
			hasOldServices = true;
		}

		if (!result[passengerId].hasOwnProperty(newService.id)) {
			result[passengerId][newService.id] = {};
		}

		segmentsList.forEach(segmentId => {
			// Remove old services.
			if (hasOldServices && result[passengerId][oldService.id].hasOwnProperty(segmentId)) {
				result[passengerId][oldService.id][segmentId] = 0;
			}

			const segmentIsAllowed = getRelatedSegments(newService.allowedSegments, segmentId).size > 0;

			if (segmentIsAllowed) {
				// Add new services.
				result[passengerId][newService.id][segmentId] = 1;
			}
		});

		return result;
	}

	return state;
};

export const selectedServicesReducer = (
	state: SelectedServicesState = {},
	action: Action<
		| ModifiedService
		| CheckinPassengerFragment[]
		| EqualizePassengerBaggagePayload
		| ReplaceServicePayload
		| ServicePayload[]
	>
): SelectedServicesState => {
	switch (action.type) {
		case SET_SERVICE:
			return setServicesReducer(state, action.payload as ServicePayload[]);

		case ADD_SERVICE:
			return addServiceReducer(state, action.payload as ModifiedService);

		case REMOVE_SERVICE:
			return removeServiceReducer(state, action.payload as ModifiedService);

		case CLEAR_SERVICE:
			return clearServiceReducer(state, action.payload as ModifiedService);

		case SET_SELECTED_SERVICES:
			return setSelectedServicesReducer(action.payload as CheckinPassengerFragment[]);

		case REPLACE_SERVICE:
			return replaceServiceReducer(state, action.payload as ReplaceServicePayload);

		case EQUALIZE_PASSENGER_BAGGAGE:
			return equalizePassengerBaggageReducer(state, action.payload as EqualizePassengerBaggagePayload);
	}

	return state;
};
